import { useImmerAtom } from 'jotai-immer';
import { ListTableStateHookReturn, TblPagination } from '../../../../core/table/table.types';
import { useCallback } from 'react';
import { tblSpecSetFilter, tblSpecSetSort } from '../../../../core/table/spec';
import { cpOrderInvoicesListColumnSpec, cpOrderInvoicesListAllColumnsView } from './CpOrderInvoicesListTableSpec';
import { tblSpecLoadView, setCurrentTableView } from '../../../../core/table/tableViewsRepo';
import {
    cpOrderInvoicesTableFiltersAtom,
    cpOrderInvoicesTableStateAtom,
    cpOrderInvoicesTableStatsAtom,
    cpOrderInvoicesTableViewAtom,
} from '../cp_order_invoices_list_atoms';
import { CP_ORDER_INVOICES_LIST_VIEW_KEY } from '../../cp_order_invoices_const';
import { getCpOrderInvoicesInitialView } from './getCpOrderInvoicesInitialView';
import { ngDelegate } from '../../../../core/services/ng.delegate';

function hasPgsize(view: any): view is { pgsize: number } {
    return typeof view.pgsize === 'number';
}

type CpOrderInvoicesListTableStateHookReturn = ListTableStateHookReturn & {
    setCurrentViewId: (viewId: string) => void;
    showHelp: () => void;
};

export const useCpOrderInvoicesListTableState = (): CpOrderInvoicesListTableStateHookReturn => {
    const [state, setState] = useImmerAtom(cpOrderInvoicesTableStateAtom);
    const [view, setView] = useImmerAtom(cpOrderInvoicesTableViewAtom);
    const [filters, setFilters] = useImmerAtom(cpOrderInvoicesTableFiltersAtom);
    const [stats, setStats] = useImmerAtom(cpOrderInvoicesTableStatsAtom);

    const setPaging = useCallback(
        (paging: Partial<TblPagination>): void => {
            setStats(draft => {
                if (paging.page !== undefined) {
                    draft.paging.page = paging.page;
                }
                if (paging.size !== undefined) {
                    draft.paging.size = paging.size;
                }
                if (paging.total !== undefined) {
                    draft.paging.total = paging.total;
                }
            });
        },
        [setStats],
    );

    const setSort = useCallback(
        (col: string, dir?: any): void => {
            setView(draft => {
                draft.sort = tblSpecSetSort(col, view.cols, dir);
            });
        },
        [setView, view.cols],
    );

    const setFilter = useCallback(
        (key: string, val: any): void => {
            setFilters(draft => tblSpecSetFilter(draft, key, view.cols, val));
        },
        [setFilters, view.cols],
    );

    const setWait = useCallback(
        (waiting: boolean): void => {
            setState(draft => {
                draft.waiting = waiting;
            });
        },
        [setState],
    );

    const setCurrentViewId = useCallback(
        (viewId: string): void => {
            setState(draft => {
                draft.currentViewId = viewId;
            });
            let newViewSpec = state.views.find(v => v.key === viewId || (v as any).id === viewId);
            if (newViewSpec && !('sections' in newViewSpec)) {
                newViewSpec = cpOrderInvoicesListAllColumnsView();
            }
            if (!newViewSpec && (viewId === '1' || viewId === 'default' || viewId === 'all')) {
                newViewSpec = state.views.find(
                    v =>
                        v.key === '1' ||
                        v.key === 'default' ||
                        v.key === 'all' ||
                        (v as any)?.id === '1' ||
                        (v as any).id === 'default' ||
                        (v as any).id === 'all',
                );
                if (!newViewSpec) {
                    newViewSpec = cpOrderInvoicesListAllColumnsView();
                    setView((draft: any) => {
                        const sortby = typeof newViewSpec?.sortby === 'string' ? newViewSpec.sortby : '';
                        draft.sort = {
                            col: sortby.replace(/^-/, ''),
                            dir: sortby.startsWith('-') ? 'DSC' : 'ASC',
                        };
                    });
                }
            }
            if (newViewSpec) {
                const allCols = Array.isArray(newViewSpec.sections)
                    ? newViewSpec.sections.flatMap((section: any) => (Array.isArray(section.cols) ? section.cols : []))
                    : [];
                const cpOrderInvoicesColMap = cpOrderInvoicesListColumnSpec();
                const colMap = new Map();
                allCols.forEach(colKey => {
                    if (cpOrderInvoicesColMap.has(colKey)) {
                        colMap.set(colKey, cpOrderInvoicesColMap.get(colKey));
                    }
                });

                const loadedView = tblSpecLoadView(viewId, [newViewSpec], colMap, getCpOrderInvoicesInitialView);
                setView((draft: any) => {
                    if (loadedView) {
                        Object.assign(draft, loadedView);
                    }
                });
                if (hasPgsize(newViewSpec) && newViewSpec.pgsize > 0) {
                    setStats(draft => {
                        draft.paging.size = newViewSpec.pgsize;
                    });
                }
            }
            setCurrentTableView(CP_ORDER_INVOICES_LIST_VIEW_KEY, viewId).catch(e => {
                console.warn('Failed to persist current view selection:', e);
            });
        },
        [setState, setView, setStats, state.views],
    );

    const showHelp = useCallback((): void => {
        ngDelegate.helpLink('order-cp-order-invoices', 'CP Order Invoices List');
    }, []);

    return {
        waiting: state.waiting,
        views: state.views,
        currentViewId: state.currentViewId,
        cols: view.cols,
        sections: view.sections,
        sort: view.sort,
        filters: filters,
        fltStats: stats.fltStats,
        paging: stats.paging,
        showHelp,
        setWait,
        setSort,
        setFilter,
        setPaging,
        setCurrentViewId,
    };
};
